import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLanguageAwareRender } from '../hooks/useLanguageAwareRender';
import {
  Typography,
  Paper,
  List,
  Divider,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { t } from '../utils/localization/localization';
import { useVersionComparison } from '../hooks/useVersionComparison';
import VersionListItem from './version/VersionListItem';
import VersionChangeDetailsDialog from './version/VersionChangeDetailsDialog';
import { logger } from '../utils/logger';

// Component to display version history for CV, Job Skills, or Company Research
const VersionHistory = ({
  versions,
  type = 'cv', // 'cv', 'job', or 'company'
  onViewVersion,
  onRestoreVersion,
  canEdit
}) => {
  // Get language context for re-rendering on language change
  const { currentLanguage: _currentLanguage, renderTrigger: _renderTrigger } = useLanguage();

  // Use standardized language-aware render hook
  const languageRenderKey = useLanguageAwareRender('VersionHistory');

  // State management
  const [confirmRestoreDialog, setConfirmRestoreDialog] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState(null);
  const [changeDetailsDialog, setChangeDetailsDialog] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);

  // Use custom hook for version comparison
  const processedVersions = useVersionComparison(versions, type);

  // Debug logging for VersionHistory component
  logger.info('[VersionHistory]', 'Component render state', {
    inputVersionsLength: versions?.length || 0,
    processedVersionsLength: processedVersions?.length || 0,
    type,
    hasVersions: !!versions,
    hasProcessedVersions: !!processedVersions,
    processedVersionsArray: processedVersions?.map(v => ({
      version: v.version,
      hasData: !!v.data,
      hasComparisonData: !!v.comparisonData
    })) || []
  });

  // Determine which version is currently being viewed
  const currentViewingVersion = processedVersions.length > 0 ? processedVersions[0].version : null;

  // Function to render change summary chips
  const renderChangeSummaryChips = (summary) => {
    const chips = [];

    // Parse the summary to extract counts
    const addedMatch = summary.match(/(\d+) added/);
    const modifiedMatch = summary.match(/(\d+) modified/);
    const removedMatch = summary.match(/(\d+) removed/);

    if (addedMatch && parseInt(addedMatch[1]) > 0) {
      chips.push(
        <Chip
          key="added"
          label={`+${addedMatch[1]}`}
          size="small"
          color="success"
          icon={<AddCircleIcon />}
        />
      );
    }

    if (modifiedMatch && parseInt(modifiedMatch[1]) > 0) {
      chips.push(
        <Chip
          key="modified"
          label={`±${modifiedMatch[1]}`}
          size="small"
          color="primary"
          icon={<CompareArrowsIcon />}
        />
      );
    }

    if (removedMatch && parseInt(removedMatch[1]) > 0) {
      chips.push(
        <Chip
          key="removed"
          label={`-${removedMatch[1]}`}
          size="small"
          color="error"
          icon={<RemoveCircleIcon />}
        />
      );
    }

    return chips;
  };

  // Handle restore version action
  const handleRestoreClick = (version) => {
    logger.info('[VersionHistory] Restore version clicked:', version.versionNumber);
    setVersionToRestore(version);
    setConfirmRestoreDialog(true);
  };

  const handleRestoreConfirm = () => {
    if (versionToRestore && onRestoreVersion) {
      logger.info('[VersionHistory] Restoring version:', versionToRestore.versionNumber);
      onRestoreVersion(versionToRestore); // Pass the full version object, not just versionToRestore.version
    }
    setConfirmRestoreDialog(false);
    setVersionToRestore(null);
  };

  // Handle view version action
  const handleViewVersion = (version) => {
    logger.info('[VersionHistory] View version clicked:', version.versionNumber);
    if (onViewVersion) {
      onViewVersion(version); // Pass the full version object, not just version.version
    }
  };

  // Handle show change details
  const handleShowDetails = (version) => {
    logger.info('[VersionHistory] Show details clicked:', version.versionNumber);
    setSelectedVersion(version);
    setChangeDetailsDialog(true);
  };

  // Get title based on type
  const getTitle = () => {
    switch (type) {
      case 'cv':
        return t('versionHistory.title');
      case 'job':
        return t('versionHistory.title');
      case 'company':
        return t('versionHistory.title');
      default:
        return t('versionHistory.title');
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }} key={languageRenderKey}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {getTitle()}
      </Typography>

      {processedVersions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('versionHistory.noVersions')}
        </Typography>
      ) : (
        <List>
          {processedVersions.map((version, index) => (
            <React.Fragment key={version.version}>
              {index > 0 && <Divider />}
              <VersionListItem
                version={version}
                index={index}
                isNewest={index === 0}
                isViewing={version.version === currentViewingVersion}
                canEdit={canEdit}
                onView={handleViewVersion}
                onRestore={handleRestoreClick}
                onShowDetails={handleShowDetails}
                renderChangeSummaryChips={renderChangeSummaryChips}
              />
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={confirmRestoreDialog}
        onClose={() => setConfirmRestoreDialog(false)}
        aria-labelledby="restore-dialog-title"
      >
        <DialogTitle id="restore-dialog-title">
          {t('versionHistory.confirmRestore')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('versionHistory.confirmRestoreMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRestoreDialog(false)}>
            {t('versionHistory.cancel')}
          </Button>
          <Button onClick={handleRestoreConfirm} autoFocus color="primary">
            {t('versionHistory.restore')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Details Dialog */}
      <VersionChangeDetailsDialog
        open={changeDetailsDialog}
        onClose={() => setChangeDetailsDialog(false)}
        selectedVersion={selectedVersion}
        type={type}
        onViewVersion={onViewVersion}
      />
    </Paper>
  );
};

export default VersionHistory;
